import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from "@mui/material";
import LockOutlined from "@mui/icons-material/LockOutlined";
import LockOpenRounded from "@mui/icons-material/LockOpenRounded";
import { MonitorButton, MonitorDialog } from "../../components/MonitorControls";
import { v } from "../../utils/helpers";
import StageErrorAlert from "./StageErrorAlert";

const REASON_MIN = 3, REASON_MAX = 500;
const UNLOCKED = { locked: false, revision: 0, by: null, at: null, reason: null };

/**
 * PROD-Sperre eines Pakets in der vorhandenen Paketverwaltung (Vertrag 9, 16.2).
 * Wie der Freeze nur für Admins bedienbar; eine gesetzte Sperre bleibt für alle mit Begründung sichtbar.
 */
export default function ProdLockControl({ item, spaceId, canManage, onSave, returnFocusRef }) {
  const { t } = useTranslation("freigabe");
  const titleId = useId();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const sending = useRef(false);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const lock = item?.prodLock || UNLOCKED;
  const locking = !lock.locked;
  const trimmed = reason.trim();
  const reasonValid = locking ? trimmed.length >= REASON_MIN && reason.length <= REASON_MAX : reason.length <= REASON_MAX;
  const name = item?.packageName || item?.packageId;

  const triggerRef = useRef(null);
  // Fokus nach dem Schließen zurück an den Auslöser oder an die von der Seite übergebene Stelle (wie PromotionFreeze).
  const restoreFocus = () => requestAnimationFrame(() => (returnFocusRef?.current || triggerRef.current)?.focus?.());
  const close = () => { if (!sending.current) { setOpen(false); restoreFocus(); } };
  const start = () => {
    if (!canManage || sending.current) return;
    setReason(""); setError(null); setOpen(true);
  };
  async function submit() {
    if (!canManage || !reasonValid || sending.current || typeof onSave !== "function") return;
    sending.current = true; setBusy(true); setError(null);
    try {
      await onSave({ packageId: item.packageId, locked: locking, revision: lock.revision ?? 0, reason: trimmed || null });
      if (alive.current) { setOpen(false); restoreFocus(); }
    } catch (failure) {
      // Überschrift aus dem Code, darunter die Servermeldung (StageErrorAlert).
      if (alive.current) setError({ code: failure?.code || null, message: failure?.message || "", details: failure?.details });
    } finally {
      sending.current = false;
      if (alive.current) setBusy(false);
    }
  }

  return <Stack spacing={0.75} alignItems="flex-start">
    {lock.locked && <Typography variant="body2" sx={{ color: v("warning"), fontWeight: 600 }}>
      {lock.reason ? t("stageBranches.prodLock.active", { reason: lock.reason }) : t("stageBranches.prodLock.activeNoReason")}
    </Typography>}
    {canManage && <MonitorButton ref={triggerRef} variant="outlined" size="small" aria-haspopup="dialog" disabled={busy}
      startIcon={lock.locked ? <LockOpenRounded/> : <LockOutlined/>} onClick={start}>
      {t(lock.locked ? "stageBranches.prodLock.lift" : "stageBranches.prodLock.set")}
    </MonitorButton>}
    {open && <MonitorDialog className="promotion-dialog sb-dialog" maxWidth="sm" aria-labelledby={titleId} onClose={close}>
      <DialogTitle id={titleId}>{t(locking ? "stageBranches.prodLock.set" : "stageBranches.prodLock.lift")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body1" sx={{ color: v("text-1"), fontWeight: 600 }}>{name}</Typography>
          <Typography variant="body2" sx={{ color: v("text-2") }}>
            {t(locking ? "stageBranches.prodLock.setHint" : "stageBranches.prodLock.liftHint")}
          </Typography>
          <TextField multiline minRows={2} fullWidth required={locking} disabled={busy} value={reason}
            label={t(locking ? "stageBranches.prodLock.reason" : "stageBranches.prodLock.reasonOptional")}
            error={reason.length > 0 && !reasonValid}
            helperText={t("stageBranches.prodLock.reasonHelp", { count: trimmed.length })}
            onChange={event => setReason(event.target.value)}/>
          <StageErrorAlert error={error} title={t("stageBranches.prodLock.writeError")}/>
        </Stack>
      </DialogContent>
      <DialogActions>
        <MonitorButton autoFocus disabled={busy} onClick={close}>{t("stageBranches.prodLock.cancel")}</MonitorButton>
        <MonitorButton variant="contained" color={locking ? "warning" : "primary"} disabled={busy || !reasonValid} onClick={submit}>
          {t(busy ? "stageBranches.prodLock.saving" : locking ? "stageBranches.prodLock.confirmSet" : "stageBranches.prodLock.confirmLift")}
        </MonitorButton>
      </DialogActions>
    </MonitorDialog>}
  </Stack>;
}
